/**
 * RedoReasonsSection - Configure redo reasons per organization
 *
 * Self-contained component that manages its own data fetching.
 * Follows the same pattern as ProductTypeAliasSection.
 *
 * Features:
 * - Add/remove/reorder reasons
 * - Auto-generates snake_case value from label
 * - Reset to defaults button
 * - Touch-friendly (44px+ targets)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  Save,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface RedoReason {
  value: string;
  label: string;
  description: string;
}

const DEFAULT_REDO_REASONS: RedoReason[] = [
  { value: 'print_defect', label: 'Print Defect', description: 'Colors wrong, missing areas, bleeding' },
  { value: 'press_issue', label: 'Press Issue', description: 'Heat press marks, creases, burns' },
  { value: 'stain_mark', label: 'Stain/Mark', description: 'Dirty or marked garment' },
  { value: 'misprint', label: 'Misprint', description: 'Wrong design or position' },
  { value: 'other', label: 'Other', description: 'Unlisted issue or special case' },
];

function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

interface RedoReasonsSectionProps {
  embedded?: boolean;
}

export function RedoReasonsSection({ embedded = false }: RedoReasonsSectionProps) {
  const { t } = useTranslation('settings');
  const [reasons, setReasons] = useState<RedoReason[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // New reason form state
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Query
  const reasonsQuery = trpc.productionSettings.getRedoReasons.useQuery();

  // Mutation
  const updateMutation = trpc.productionSettings.updateRedoReasons.useMutation({
    onSuccess: () => {
      toast.success(t('production.redoReasons.saved'));
      setHasChanges(false);
      reasonsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Sync local state with server data
  useEffect(() => {
    if (reasonsQuery.data) {
      setReasons(reasonsQuery.data.reasons);
      setHasChanges(false);
    }
  }, [reasonsQuery.data]);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;

    const value = toSnakeCase(label);
    if (!value) {
      toast.error('Label must contain at least one letter or number');
      return;
    }

    if (reasons.some(r => r.value === value)) {
      toast.error(`A reason with value "${value}" already exists`);
      return;
    }

    if (reasons.length >= 20) {
      toast.error('Maximum of 20 redo reasons allowed');
      return;
    }

    setReasons(prev => [...prev, { value, label, description: newDescription.trim() }]);
    setNewLabel('');
    setNewDescription('');
    setHasChanges(true);
  };

  const handleRemove = (value: string) => {
    if (reasons.length <= 1) {
      toast.error('At least one redo reason is required');
      return;
    }
    setReasons(prev => prev.filter(r => r.value !== value));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setReasons(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index - 1]!;
      next[index - 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === reasons.length - 1) return;
    setReasons(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index + 1]!;
      next[index + 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleResetToDefaults = () => {
    setReasons(DEFAULT_REDO_REASONS);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ reasons });
  };

  const isLoading = reasonsQuery.isLoading;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure the redo reasons available in the catcher station dialog.
        The order here determines the display order.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Existing reasons table */}
          {reasons.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">Label</th>
                    <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Description</th>
                    <th className="w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((reason, index) => (
                    <tr key={reason.value} className="border-b last:border-b-0">
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium">{reason.label}</span>
                          <span className="text-xs text-muted-foreground ml-2 font-mono">{reason.value}</span>
                        </div>
                        {/* Show description on mobile below the label */}
                        {reason.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 sm:hidden">{reason.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                        {reason.description || '-'}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            aria-label={`Move ${reason.label} up`}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === reasons.length - 1}
                            aria-label={`Move ${reason.label} down`}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleRemove(reason.value)}
                            aria-label={`Remove ${reason.label}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add new reason form */}
          <div className="flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-reason-label" className="text-sm text-muted-foreground">
                Label
              </Label>
              <Input
                id="new-reason-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Wrong Color"
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-reason-desc" className="text-sm text-muted-foreground">
                Description (optional)
              </Label>
              <Input
                id="new-reason-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Incorrect color garment"
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
            </div>
            <Button
              variant="outline"
              className="h-11"
              onClick={handleAdd}
              disabled={!newLabel.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Empty state */}
          {reasons.length === 0 && (
            <p className="text-sm text-muted-foreground/70 text-center py-2">
              No reasons configured. Add at least one redo reason.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetToDefaults}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('production.redoReasons.resetToDefaults')}
            </Button>

            {hasChanges && (
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending || reasons.length === 0}
                className="h-12 gap-2"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t('production.common.saveChanges')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="border rounded-lg p-4">
      <h3 className="font-medium mb-3">Redo Reasons</h3>
      {content}
    </div>
  );
}
